// CreateUser.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormRegisterScheme } from "../../../Models/Schemas/FormRegisterScheme";
import { FORM_REGISTER } from "../../../Constants/Register";
import Button from "../../Elements/Button/Button";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import useForm from "../../../Hooks/useForm";
import Input from "../../Elements/Input/Input";
import Loader from "../../Elements/Loader/Loader";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import { useSearchParams } from "react-router-dom";
import MapPickerLeaflet from "../createRestaurant/MapPickerLeaflet/MapPickerLeaflet";
import VerifyEmailModal from "./VerifyEmail/VerifyEmailModal";
import "./createUser.css";

const CreateUser = () => {
  const showToast = useToast();

  // Acciones/flags estrictamente necesarios (evita resuscripciones que causen loops)
  const fetchDataRegister = useStore((s) => s.fetchDataRegister);
  const fetchDataSendOtp = useStore((s) => s.fetchDataSendOtp);
  const fetchDataVerifyEmail = useStore((s) => s.fetchDataVerifyEmail);
  const loading = useStore((s) => s.loading);

  // Flags del flujo OTP (para pintar modal)
  const otpSending = useStore((s) => s.otpSending);
  const otpVerifying = useStore((s) => s.otpVerifying);
  const otpError = useStore((s) => s.otpError);
  const otpSuccess = useStore((s) => s.otpSuccess);
  const otpCooldownUntil = useStore((s) => s.otpCooldownUntil);

  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get("role") || "cliente";

  const {
    values, errors, touched, handleBlur, handleChange, setFieldValue,
  } = useForm(FORM_REGISTER, FormRegisterScheme());

  const canSubmit = useMemo(() => {
    try {
      FormRegisterScheme().validateSync(values, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }, [values]);

  // Mapa
  const [showMap, setShowMap] = useState(false);
  const lastGeoRef = useRef({ q: "", lat: null, lng: null });

  // Modal y control de reenvío OTP por apertura
  const [verifyOpen, setVerifyOpen] = useState(false);
  const otpRequestedRef = useRef(false);

  // Email pendiente (para reabrir modal si se cerró)
  const [pendingEmail, setPendingEmail] = useState(null);

  // Fija el role desde query string
  useEffect(() => {
    if (values.role !== roleParam) {
      setFieldValue("role", roleParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleParam]);

  const setLatLngFromMap = (lat, lng, addr) => {
    handleChange("lat")({ target: { name: "lat", value: String(lat) } });
    handleChange("lng")({ target: { name: "lng", value: String(lng) } });
    if (addr) {
      handleChange("address")({ target: { name: "address", value: addr } });
      lastGeoRef.current.q = addr;
    }
    setShowMap(false);
  };

  // Normaliza la respuesta del registro para leer status/message/requireVerification/email
  const normalizeRegisterResp = (res) => {
    const status = res?.statusCode ?? res?.code ?? null;
    const body = res?.data ?? res ?? {};
    const message =
      body?.message ?? res?.message ?? (status >= 200 && status < 300 ? "OK" : "Error");
    const requireVerification = body?.requireVerification ?? res?.requireVerification ?? false;
    const emailFromBody = body?.email ?? res?.email ?? null;
    return { status, message, requireVerification, emailFromBody };
  };

  // Al montar: si hay pendingEmail guardado, confirmamos con el backend si sigue pendiente.
  useEffect(() => {
    (async () => {
      try {
        const pending = localStorage.getItem("pendingEmail");
        if (!pending) return;

        const res = await fetchDataSendOtp(pending);
        const status = res?.statusCode ?? res?.code;
        const msg = String(res?.message || res?.data?.message || "").toLowerCase();

        const alreadyVerified =
          status === 409 || msg.includes("verific") || msg.includes("ya verificado");

        if (alreadyVerified) {
          try { localStorage.removeItem("pendingEmail"); } catch {}
          setPendingEmail(null);
          setVerifyOpen(false);
          otpRequestedRef.current = false;
          return;
        }

        const ok = status === 200;
        if (ok) {
          setPendingEmail(pending);
          setVerifyOpen(true);
          otpRequestedRef.current = true;
        }
      } catch {
        // en caso de error silencioso, no abrimos el modal
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Registro + abrir modal + enviar OTP 1 vez
  const submit = async () => {
    const payload = {
      name: values.name,
      email: values.email,
      password: values.password,
      phone: values.phone,
      role: values.role,
      delivery: {
        direccion: values.address || "",
        lat: values.lat ? Number(values.lat) : null,
        lng: values.lng ? Number(values.lng) : null,
      },
    };

    const res = await fetchDataRegister(payload);
    const { status, message, requireVerification, emailFromBody } = normalizeRegisterResp(res);
    const emailForOtp = (emailFromBody || values.email || "").trim();

    if (status === 201) {
      showToast("success", message || "Usuario creado correctamente");
      setVerifyOpen(true);
      setPendingEmail(emailForOtp);
      try { localStorage.setItem("pendingEmail", emailForOtp); } catch { }

      otpRequestedRef.current = false;
      if (emailForOtp && !otpRequestedRef.current) {
        otpRequestedRef.current = true;
        await fetchDataSendOtp(emailForOtp);
      }
      return;
    }

    if (status === 200 && requireVerification) {
      // Usuario ya existía pero no verificado
      showToast("warning", message || "Usuario pendiente de verificación");
      setVerifyOpen(true);
      setPendingEmail(emailForOtp);
      try { localStorage.setItem("pendingEmail", emailForOtp); } catch { }

      otpRequestedRef.current = false;
      if (emailForOtp && !otpRequestedRef.current) {
        otpRequestedRef.current = true;
        await fetchDataSendOtp(emailForOtp);
      }
      return;
    }

    // Otros casos (400/409/etc.)
    showToast("error", message || "No se pudo crear usuario");
  };

  if (loading) return <Loader />;

  // email a usar en el modal (si hay pendingEmail lo preferimos)
  const modalEmail = (pendingEmail || values.email || "").trim();

  return (
    <div className="register-wrapper flex justify-center align-center">
      <div className="form-register flex justify-center">
        <div>
          <h4>Crear nueva cuenta</h4>
        </div>

        <div className="mt-3">
          <form onSubmit={(e) => e.preventDefault()}>
            <Input
              value={values.name}
              label="Nombre"
              onChange={handleChange("name")}
              onBlur={handleBlur("name")}
              error={setErrorFormSimpleUtil(errors, touched, "name")}
              success={!setErrorFormSimpleUtil(errors, touched, "name")}
            />
            <Input
              value={values.email}
              label="Correo"
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              error={setErrorFormSimpleUtil(errors, touched, "email")}
              success={!setErrorFormSimpleUtil(errors, touched, "email")}
              type="email"
            />
            <Input
              value={values.password}
              label="Contraseña"
              onChange={handleChange("password")}
              onBlur={handleBlur("password")}
              error={setErrorFormSimpleUtil(errors, touched, "password")}
              success={!setErrorFormSimpleUtil(errors, touched, "password")}
              variant="Password"
            />
            <Input
              value={values.confirmPassword}
              label="Confirmar contraseña"
              onChange={handleChange("confirmPassword")}
              onBlur={handleBlur("confirmPassword")}
              error={setErrorFormSimpleUtil(errors, touched, "confirmPassword")}
              success={!setErrorFormSimpleUtil(errors, touched, "confirmPassword")}
              variant="Password"
            />
            <Input
              value={values.phone}
              label="Teléfono"
              onChange={handleChange("phone")}
              onBlur={handleBlur("phone")}
              error={setErrorFormSimpleUtil(errors, touched, "phone")}
              success={!setErrorFormSimpleUtil(errors, touched, "phone")}
              inputMode="numeric"
              pattern="\d*"
              maxLength={14}
            />

            <Input
              value={values.address}
              label="Dirección"
              onClick={() => setShowMap(true)}
              readOnly
              error={setErrorFormSimpleUtil(errors, touched, "address")}
              success={!setErrorFormSimpleUtil(errors, touched, "address")}
              placeholder="Selecciona tu dirección en el mapa"
            />

            {showMap && (
              <MapPickerLeaflet
                initialLat={values.lat ? Number(values.lat) : 19.432608}
                initialLng={values.lng ? Number(values.lng) : -99.133209}
                geocodeEmail="tu-correo@dominio.com"
                onPick={setLatLngFromMap}
                onClose={() => setShowMap(false)}
              />
            )}

            <center>
              <Button
                text="Registrarse"
                color="blue"
                fill
                action={submit}
                disabled={!canSubmit}
                classNames="mt-3"
              />
              <Button
                text="Ya tengo cuenta"
                color="gray"
                fill
                action={() => (window.location.href = "/")}
                classNames="mt-3"
              />
            </center>
          </form>

          {/* Botón para reabrir/verificar manualmente si cerraste el modal */}
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <button
              type="button"
              className="link-button"
              onClick={async () => {
                const mail = (values.email || pendingEmail || "").trim();
                if (!mail) return showToast("error", "Escribe tu correo arriba primero");
                try {
                  const res = await fetchDataSendOtp(mail);
                  const status = res?.statusCode ?? res?.code;
                  const msg = String(res?.message || res?.data?.message || "").toLowerCase();

                  const alreadyVerified =
                    status === 409 || msg.includes("verific") || msg.includes("ya verificado");

                  if (alreadyVerified) {
                    try { localStorage.removeItem("pendingEmail"); } catch {}
                    setPendingEmail(null);
                    setVerifyOpen(false);
                    otpRequestedRef.current = false;
                    return showToast("success", "Tu correo ya está verificado. Inicia sesión.");
                  }

                  if (status === 200) {
                    setPendingEmail(mail);
                    try { localStorage.setItem("pendingEmail", mail); } catch {}
                    otpRequestedRef.current = true;
                    setVerifyOpen(true);
                    return;
                  }

                  showToast("error", res?.message || "No se pudo enviar el código");
                } catch (e) {
                  showToast("error", e?.message || "No se pudo enviar el código");
                }
              }}
            >
              ¿Ya te registraste? Verificar mi correo
            </button>
          </div>
        </div>
      </div>

      {/* Modal de verificación por OTP */}
      <VerifyEmailModal
        open={verifyOpen}
        email={modalEmail}
        onClose={() => {
          setVerifyOpen(false);
          // NO borramos pendingEmail para poder reabrir luego
          otpRequestedRef.current = false;
        }}
        onVerified={() => {
          try { localStorage.removeItem("pendingEmail"); } catch { }
          setPendingEmail(null);
          showToast("success", "¡Correo verificado! Ya puedes iniciar sesión.");
          setTimeout(() => (window.location.href = "/"), 1200);
        }}
        onResend={() => {
          const mail = (modalEmail || "").trim();
          return mail && fetchDataSendOtp(mail);
        }}
        onVerify={(otp) => {
          const mail = (modalEmail || "").trim();
          return mail && fetchDataVerifyEmail({ email: mail, otp });
        }}
        otpSending={otpSending}
        otpVerifying={otpVerifying}
        otpError={otpError}
        otpSuccess={otpSuccess}
        otpCooldownUntil={otpCooldownUntil}
      />
    </div>
  );
};

export default CreateUser;